import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_LABELS, CONTACT_KINDS, type Account, type AccountKind, type Contact, type ContactKind } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { ContactPicker, type PickedContact } from '../../components/ContactPicker';
import { useSession } from '../../session';
import { accountPath } from './AccountsPage';
import './invoices.css';
import './purchase.css';

const KIND_LABELS: Record<ContactKind, string> = { company: 'Firma', person: 'Person' };

/**
 * Neuen Debitor oder Kreditor anlegen (KON-161): entweder einen vorhandenen Kontakt wählen, der noch kein Konto dieser Art hat, oder gleich einen neuen Kontakt mit
 * seinen Finanzdaten anlegen. Mit ?kontakt=… ist der Kontakt vorgewählt.
 */
export function AccountCreatePage({ kind }: { kind: AccountKind }) {
  const label = ACCOUNT_LABELS[kind];
  const base = accountPath(kind);
  const endpoint = kind === 'debtor' ? '/debtors' : '/creditors';
  const { can } = useSession();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [params] = useSearchParams();
  const preset = params.get('kontakt');
  const mayCreateContact = can('contacts.write');

  const presetContact = useQuery({ queryKey: ['contact', preset], queryFn: async () => (await api<{ data: Contact }>('GET', `/contacts/${preset}`)).data, enabled: !!preset });
  const [mode, setMode] = useState<'existing' | 'new'>(preset || !mayCreateContact ? 'existing' : 'new');
  const [picked, setPicked] = useState<PickedContact | null>(null);
  const [form, setForm] = useState({ kind: 'company' as ContactKind, name: '', street: '', postalCode: '', city: '', email: '', phone: '', vatId: '', iban: '', bic: '' });
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));
  const chosen = picked ?? (presetContact.data ? { id: presetContact.data.id, name: presetContact.data.name } : null);

  const save = useMutation({
    mutationFn: async () => {
      if (mode === 'existing') return (await api<{ data: Account }>('POST', endpoint, { contactId: chosen?.id })).data;
      const finance = Object.fromEntries(Object.entries({ vatId: form.vatId.trim(), iban: form.iban.trim(), bic: form.bic.trim() }).filter(([, v]) => v));
      return (
        await api<{ data: Account }>('POST', endpoint, {
          contact: { kind: form.kind, name: form.name.trim(), street: form.street.trim(), postalCode: form.postalCode.trim(), city: form.city.trim(), email: form.email.trim(), phone: form.phone.trim() },
          ...(Object.keys(finance).length > 0 ? { finance } : {}),
        })
      ).data;
    },
    onSuccess: (account) => {
      client.invalidateQueries({ queryKey: ['accounts', kind] });
      client.invalidateQueries({ queryKey: ['contacts'] });
      navigate(`${base}/${account.id}`, { replace: true });
    },
    onError: (e) => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setError('Bitte prüfen Sie die markierten Felder.');
      } else setError(e instanceof ApiError ? e.message : `Der ${label.singular} konnte nicht angelegt werden.`);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setErrors({});
    if (mode === 'existing' && !chosen) return setError('Bitte wählen Sie einen Kontakt.');
    save.mutate();
  }
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <>
      <p><Link to={base} className="back-link">← Alle {label.plural}</Link></p>
      <div className="page-head"><h1>Neuer {label.singular}</h1></div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <form className="card account-form" onSubmit={submit} noValidate>
        {mayCreateContact && (
          <div className="account-mode" role="tablist" aria-label="Kontakt">
            <button type="button" role="tab" aria-selected={mode === 'new'} className={`viewer__tab${mode === 'new' ? ' is-active' : ''}`} onClick={() => setMode('new')}>Neuer Kontakt</button>
            <button type="button" role="tab" aria-selected={mode === 'existing'} className={`viewer__tab${mode === 'existing' ? ' is-active' : ''}`} onClick={() => setMode('existing')}>Vorhandener Kontakt</button>
          </div>
        )}
        {mode === 'existing' ? (
          <div className="field">
            <span>Kontakt</span>
            <ContactPicker value={chosen} onChange={setPicked} label="Kontakt" invalid={!!errors.contactId} />
            {err('contactId')}
            <small className="muted">Ein Kontakt kann Kunde und Lieferant zugleich sein. Hat er schon ein Konto dieser Art, meldet Wirkia das mit der Nummer.</small>
          </div>
        ) : (
          <>
            <p className="muted small">Wirkia legt den Kontakt in den Kontakten an und gibt ihm das Konto. Die Nummer vergibt Wirkia aus dem Nummernkreis, sie ändert sich nie.</p>
            <div className="form-grid">
              <label className="field">
                <span>Art</span>
                <select className="input" value={form.kind} onChange={(e) => set('kind', e.target.value as ContactKind)}>
                  {CONTACT_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
                </select>
              </label>
              <label className="field field--wide">
                <span>Name</span>
                <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={120} aria-invalid={!!errors['contact.name']} autoFocus />
                {err('contact.name')}
              </label>
              <label className="field field--wide">
                <span>Straße und Hausnummer</span>
                <input className="input" value={form.street} onChange={(e) => set('street', e.target.value)} maxLength={120} />
              </label>
              <label className="field">
                <span>PLZ</span>
                <input className="input" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} maxLength={10} />
              </label>
              <label className="field">
                <span>Ort</span>
                <input className="input" value={form.city} onChange={(e) => set('city', e.target.value)} maxLength={80} />
              </label>
              <label className="field">
                <span>E-Mail</span>
                <input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} maxLength={200} aria-invalid={!!errors['contact.email']} />
                {err('contact.email')}
              </label>
              <label className="field">
                <span>Telefon</span>
                <input className="input" value={form.phone} onChange={(e) => set('phone', e.target.value)} maxLength={40} />
              </label>
              <label className="field">
                <span>USt-IdNr.</span>
                <input className="input" value={form.vatId} placeholder="DE123456789" onChange={(e) => set('vatId', e.target.value)} aria-invalid={!!errors['finance.vatId']} />
                {err('finance.vatId')}
              </label>
              <label className="field">
                <span>IBAN</span>
                <input className="input" value={form.iban} onChange={(e) => set('iban', e.target.value)} aria-invalid={!!errors['finance.iban']} />
                {err('finance.iban')}
              </label>
              <label className="field">
                <span>BIC</span>
                <input className="input" value={form.bic} onChange={(e) => set('bic', e.target.value)} maxLength={11} aria-invalid={!!errors['finance.bic']} />
                {err('finance.bic')}
              </label>
            </div>
          </>
        )}
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird angelegt …' : `${label.singular} anlegen`}</button>
          <Link className="btn btn--secondary" to={base}>Abbrechen</Link>
        </div>
      </form>
    </>
  );
}
